import { useState } from "react";

import { faPaste } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { DurationWeeks } from "api/services/treatment/schemas/Treatment";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";

import type { TimelineActivity } from "../types";

interface CopyExerciseModalProps {
  currentWeek: number;
  durationWeeks: DurationWeeks;
  exercises: TimelineActivity[];
  weeks: number;
  onClose: () => void;
  onCopy: (selectedWeeks: number[]) => void;
}

export const CopyExerciseModal: React.FC<CopyExerciseModalProps> = ({
  currentWeek = 0,
  durationWeeks,
  exercises,
  weeks,
  onClose,
  onCopy,
}) => {
  const { t } = useTranslation();
  const [selectedWeeks, setSelectedWeeks] = useState<Set<number>>(new Set());

  const startWeek = Math.max(1, currentWeek);
  const availableWeeks = Array.from({ length: durationWeeks || weeks - startWeek + 1 }, (_, i) => startWeek + i);

  const toggleWeek = (week: number) => {
    setSelectedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(week)) {
        newSet.delete(week);
      } else {
        newSet.add(week);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (selectedWeeks.size === availableWeeks.length) {
      setSelectedWeeks(new Set());
    } else {
      setSelectedWeeks(new Set(availableWeeks));
    }
  };

  const handleCopy = () => {
    if (selectedWeeks.size > 0) {
      onCopy(Array.from(selectedWeeks));
      onClose();
    }
  };

  return (
    <MUIUnifiedModal
      open
      onClose={onClose}
      width="500px"
      title={
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ color: "#0066cc" }}>
          <FontAwesomeIcon icon={faPaste} />
          <Typography variant="h4">{t("treatment.editor.copy_exercise.title")}</Typography>
        </Stack>
      }
    >
      <ExerciseInfo>
        {exercises.length === 1 ? (
          <>
            {exercises[0].image_url && <ExerciseImage src={exercises[0].image_url} alt={exercises[0].title} />}
            <ExerciseDetails>
              <ExerciseName>{exercises[0].title}</ExerciseName>
              <ExerciseMeta>Level {exercises[0].level}</ExerciseMeta>
            </ExerciseDetails>
          </>
        ) : (
          <ExerciseDetails>
            <ExerciseName>
              {t("treatment.editor.copy_exercise.exercises_selected", { count: exercises.length })}
            </ExerciseName>
            <ExerciseMeta>{exercises.map(e => e.title).join(", ")}</ExerciseMeta>
          </ExerciseDetails>
        )}
      </ExerciseInfo>

      <Section>
        <SectionHeader>
          <SectionTitle>{t("treatment.editor.copy_exercise.select_weeks")}</SectionTitle>
          <SelectAllButton onClick={toggleAll}>
            {selectedWeeks.size === availableWeeks.length
              ? t("treatment.editor.copy_exercise.deselect_all")
              : t("treatment.editor.copy_exercise.select_all")}
          </SelectAllButton>
        </SectionHeader>

        <WeekGrid>
          {availableWeeks.map(week => (
            <WeekCheckbox key={week}>
              <Checkbox
                type="checkbox"
                checked={selectedWeeks.has(week)}
                onChange={() => toggleWeek(week)}
                id={`week-${week}`}
              />
              <CheckboxLabel htmlFor={`week-${week}`}>
                {t("treatment.editor.copy_exercise.week", { week })}
                {week === currentWeek && <CurrentBadge>{t("treatment.editor.copy_exercise.current")}</CurrentBadge>}
              </CheckboxLabel>
            </WeekCheckbox>
          ))}
        </WeekGrid>
      </Section>

      <Footer>
        <CancelButton onClick={onClose}>{t("buttons.cancel")}</CancelButton>
        <PasteButton onClick={handleCopy} disabled={selectedWeeks.size === 0}>
          <FontAwesomeIcon icon={faPaste} />
          {t("treatment.editor.copy_exercise.copy_to", { count: selectedWeeks.size })}
        </PasteButton>
      </Footer>
    </MUIUnifiedModal>
  );
};

const ExerciseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: ${({ theme }) => theme.semantic.container.border};
`;

const ExerciseImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
`;

const ExerciseDetails = styled.div`
  flex: 1;
`;

const ExerciseName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #1a2332;
`;

const ExerciseMeta = styled.div`
  font-size: 13px;
  color: #5c6f82;
  margin-top: 2px;
`;

const Section = styled.div`
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a2332;
  margin: 0;
`;

const SelectAllButton = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e6f2ff;
  }
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
`;

const WeekCheckbox = styled.div`
  display: flex;
  align-items: center;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #0066cc;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  font-size: 14px;
  color: #1a2332;
  cursor: pointer;
  user-select: none;
`;

const CurrentBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  background-color: #e6f2ff;
  color: #0066cc;
  border-radius: 8px;
  text-transform: uppercase;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: ${({ theme }) => theme.semantic.container.border};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #ffffff;
  border: ${({ theme }) => theme.semantic.container.border};
  border-radius: 6px;
  color: #5c6f82;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #c5d0dd;
  }
`;

const PasteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #0066cc;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #0052a3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
